import React, { useContext, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RelPoint, RelSide, RelType } from 'src/config';
import * as types from '../../types';
import * as actions from '../../actions';
import { Box, Typography } from '@mui/material';
import IconButton from '../components/IconButton';
import Lookup from './Lookup';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import YoutubeSearchedForIcon from '@mui/icons-material/YoutubeSearchedFor';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import _ from 'lodash';
import { getLocator } from '../../utils';
import RelContainer from './components/RelContainer';
import { Scope as ExplorerScope, ExplorerContext } from '../../components/Entity';
import { getPrincipal, AccessLevel, Principal } from 'src/auth';

const RelatedOne: React.FC<{
    parentLocator: types.Locator;
    vector: RelPoint;
    relEntity?: types.Entity; // related entity (either direct or inverse).
    onLink: (ids: types.ID[]) => void;
    onUnLink: (ids: types.ID[]) => void;
    stack: types.StackItem[];
    entity?: types.Entity; // main entity (either owner or being pointed at by related - inverse).
}> = ({
    parentLocator,
    vector,
    relEntity,
    onLink,
    onUnLink,
    stack,
    entity,
}) => {
        const dispatch = useDispatch();
        const principal: Principal = getPrincipal();

        // main entity locked
        const isLocked: boolean = entity?.locked || false;

        const { side: dir, rel } = vector;
        const inverse: boolean = (dir === RelSide.TARGET);

        const [lookup, setLookup] = useState<{ local: boolean } | undefined>();

        const resourceName = useMemo(() => inverse ? rel.resource : rel.related, [rel, inverse]);
        const userCanManage: boolean = principal.hasAccessResource(resourceName, AccessLevel.Manage);
        const attrName = useMemo(() => inverse ? rel.backReferenceName : rel.referenceName, [rel, inverse]);

        const locator: types.Locator | null = useMemo(
            () => (relEntity ? getLocator(resourceName, relEntity) : null),
            [resourceName, relEntity]
        );

        const canCreate: boolean = !(isLocked && !inverse); // isPersistent(parentLocator.id);
        const lockedAreNonSelectable: boolean = inverse;

        const editAction = relEntity ? <IconButton onClick={() => dispatch(actions.pushAction(locator!, vector))}>
            <EditIcon fontSize='small' />
        </IconButton> : null;

        const createAction = <IconButton onClick={() => {
            const port: types.RelPort = {
                relPoint: vector,
                locator: parentLocator,
                attrName,
            };

            dispatch(actions.createTemp(port));
        }}>
            <AddIcon fontSize='small' />
        </IconButton>;

        const unlinkAction = relEntity ? <IconButton onClick={() => { onUnLink([relEntity.attrs['_id'] as types.ID]) }}>
            <LinkOffIcon fontSize='small' />
        </IconButton> : null;

        const searchAction = <IconButton onClick={() => setLookup({ local: false })}>
            <SearchIcon fontSize='small' />
        </IconButton>;

        const searchLocalAction = <IconButton onClick={() => setLookup({ local: true })}>
            <YoutubeSearchedForIcon fontSize='small' />
        </IconButton>;

        const renderItemActions = (oppositeEntity: types.Entity) => {
            return userCanManage ? <>
                {editAction}
                {(oppositeEntity.locked && inverse) || (isLocked && !inverse) ? null : unlinkAction}
                {!relEntity && canCreate && createAction}
                {(isLocked && !inverse) ? null : searchAction}
            </> : <></>
        }

        const renderEmptyActions = () => {
            return userCanManage ? <Box
                sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'row-reverse',
                }}
            >
                {!relEntity && canCreate && createAction}
                {(isLocked && !inverse) ? null : searchAction}
                {(isLocked && !inverse) ? null : searchLocalAction}
            </Box> : <></>
        }

        return (
            <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontWeight: 'bold' }}>{inverse ? (rel.backReferenceLabel || rel.backReferenceName) : (rel.referenceLabel || rel.referenceName)}:</Typography>

                <RelContainer
                    vector={vector}
                    stack={stack}
                    entities={relEntity != null ? [relEntity] : []}
                    parentLocator={parentLocator}
                    renderItemActions={renderItemActions}
                    renderEmptyActions={renderEmptyActions}
                />

                <Lookup
                    rel={rel}
                    inverse={inverse}
                    open={lookup != null}
                    local={lookup?.local || false}
                    onClose={() => {
                        setLookup(undefined);
                    }}
                    onSelect={(ids: types.ID[]) => onLink(ids)}
                    lockedAreNonSelectable={lockedAreNonSelectable}
                />
            </Box>
        )
    }

export default RelatedOne;